import { SigninState, type UserManager } from 'oidc-client-ts';

/** Maps `/oauth/register` query string to registration extra params. Duplicate keys: last wins. */
export function extraParamsFromRegisterUrlSearch(search: string): Record<string, string> {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(normalized);
  const out: Record<string, string> = {};
  params.forEach((value, key) => {
    out[`sign_up_param_${key}`] = value;
  });
  return out;
}

/** Prefer app.* over account.* for registration; keep local redirect_uri unchanged. */
function getRegistrationRedirectUri(settingsRedirectUri: string): string {
  const url = new URL(settingsRedirectUri);
  if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
    return settingsRedirectUri;
  }
  if (url.hostname.startsWith('account.')) {
    url.hostname = `app.${url.hostname.slice('account.'.length)}`;
  }
  return url.toString();
}

export async function signinRegistrationRedirect(
  userManager: UserManager,
  extraParams: Record<string, string | number | boolean>,
): Promise<void> {
  const settings = userManager.settings;
  const redirectUri = getRegistrationRedirectUri(settings.redirect_uri);
  const base = settings.authority.endsWith('/') ? settings.authority : `${settings.authority}/`;
  const registrationsUrl = new URL('protocol/openid-connect/registrations', base).href;

  const extraQueryParams: Record<string, string | number | boolean> = {
    ...settings.extraQueryParams,
    ...extraParams,
  };

  const state = await SigninState.create({
    request_type: 'si:r',
    code_verifier: !settings.disablePKCE,
    client_id: settings.client_id,
    authority: settings.authority,
    redirect_uri: redirectUri,
    response_mode: settings.response_mode,
    client_secret: settings.client_secret,
    scope: settings.scope,
    extraTokenParams: settings.extraTokenParams,
  });

  const parsedUrl = new URL(registrationsUrl);
  parsedUrl.searchParams.append('client_id', settings.client_id);
  parsedUrl.searchParams.append('redirect_uri', redirectUri);
  parsedUrl.searchParams.append('response_type', settings.response_type);
  if (!settings.omitScopeWhenRequesting) {
    parsedUrl.searchParams.append('scope', settings.scope);
  }
  parsedUrl.searchParams.append('state', state.id);
  if (state.code_challenge) {
    parsedUrl.searchParams.append('code_challenge', state.code_challenge);
    parsedUrl.searchParams.append('code_challenge_method', 'S256');
  }
  const resource = settings.resource;
  if (resource) {
    const resources = Array.isArray(resource) ? resource : [resource];
    resources.forEach((r) => parsedUrl.searchParams.append('resource', r));
  }
  for (const [key, value] of Object.entries({
    response_mode: settings.response_mode,
    ...extraQueryParams,
  })) {
    if (value != null) {
      parsedUrl.searchParams.append(key, value.toString());
    }
  }

  await settings.stateStore.set(state.id, state.toStorageString());
  const navigate = window.location[settings.redirectMethod].bind(window.location) as (
    href: string,
  ) => void;
  navigate(parsedUrl.href);
}
